import { FC, useState } from 'react';

import { LoadingOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { Col, Input, Radio, Row, Select, Switch } from 'antd';
import { ErrorMessage, Formik } from 'formik';
import moment from 'moment';
import { NumericFormat } from 'react-number-format';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import * as S from './styled';

import { getVoucherCodeInUse } from '../../../hooks/vouchers';
import {
  setUsersVoucherList,
  setVoucherFormValues,
} from '../../../redux/actions/voucher';
import { FORM_VALUES_INITIAL_STATE } from '../../../redux/reducers/voucher';
import { formatNumber } from '../../../utils/masks';
import DatePickerCustom from '../../atoms/DatePickerCustom';

const MAX_QUANTITY_VOUCHER = 2000;

const rawMaskMoney = (value: string) => {
  const onlyDigits = value
    .split('')
    .filter((s) => /\d/.test(s))
    .join('')
    .padStart(3, '0');

  const digitsFloat = onlyDigits.slice(0, -2) + '.' + onlyDigits.slice(-2);

  return digitsFloat;
};

const mascaraMoeda = (value: any) => {
  const digitsFloat = rawMaskMoney(value);

  return maskCurrency(digitsFloat);
};

const maskCurrency = (valor, locale = 'pt-BR', currency = 'BRL') => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(valor);
};

const getKindUnit = (kindValue: string) => {
  const kinds: any = {
    PERCENT_RECHARGE: '%',
    MONEY_RECHARGE: '',
    KWH_RECHARGE: 'kWh',
  };

  return kinds[kindValue];
};

export type VoucherFormProps = {
  initialValues: any;
  loading: boolean;
  chargePointsList: any;
  onSubmit: any;
  isActivation: boolean;
  onSaveChanges: (values: any) => void;
  isEdit?: boolean;
};

export enum VoucherExpireType {
  ExpirationDate = 'EXPIRATION_DATE',
  UsesPerUser = 'USES_PER_USER',
}

export enum VoucherGroup {
  Activation = 'ACTIVATION',
  Guest = 'GUEST',
}

const VoucherForm: FC<VoucherFormProps> = ({
  initialValues,
  loading,
  isActivation,
  chargePointsList,
  onSubmit,
  onSaveChanges,
  isEdit,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userPks = useSelector((state: any) => state?.voucher?.userPks);

  const [showChargers, setShowChargers] = useState(
    !!initialValues?.charge_box_pks?.length,
  );
  const [showNumberOfUses, setShowNumberOfUses] = useState(
    initialValues.expire_type === VoucherExpireType.UsesPerUser,
  );
  const [showSelectUsersError, setShowSelectUsersError] = useState(false);

  const [hasCode, setHasCode] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);

  const [voucherGroup, setVoucherGroup] = useState<VoucherGroup>(
    isActivation ? VoucherGroup.Activation : VoucherGroup.Guest,
  );

  const handleVerifyCode = async (code: string) => {
    setLoadingCode(true);

    const verifyCode: any = await getVoucherCodeInUse(code);

    setLoadingCode(false);

    return verifyCode?.in_use === true;
  };

  const clearVoucherTemporaryValues = () => {
    dispatch(setUsersVoucherList([]));

    dispatch(setVoucherFormValues(FORM_VALUES_INITIAL_STATE));
  };

  const handleCancel = () => {
    clearVoucherTemporaryValues();

    history.goBack();
  };

  return (
    <Formik
      validateOnMount
      initialValues={initialValues}
      validate={(values) => {
        const errors: any = {};

        if (!values.code) {
          errors.code = 'O código é um campo obrigatório';
        }

        if (!values.title) {
          errors.title = 'O nome é um campo obrigatório';
        }

        if (!values.description) {
          errors.description = 'A descrição é um campo obrigatório';
        }

        if (!values.kind) {
          errors.kind = 'O tipo do voucher é um campo obrigatório';
        }

        if (!values.kind_value) {
          errors.kind_value = 'O valor do voucher é um campo obrigatório';
        } else if (Number(rawMaskMoney(String(values.kind_value))) === 0) {
          errors.kind_value = 'O valor do voucher não pode ser zero';
        } else if (
          values.kind === 'MONEY_RECHARGE' &&
          Number(rawMaskMoney(String(values.kind_value))) > 1000
        ) {
          errors.kind_value =
            'O valor  do voucher não pode ser superior a R$ 1.000,00';
        } else if (values.kind === 'KWH_RECHARGE' && values.kind_value > 5000) {
          errors.kind_value =
            'O valor do voucher não pode ser superior a 5000 kWh';
        } else if (
          values.kind === 'PERCENT_RECHARGE' &&
          values.kind_value > 100
        ) {
          errors.kind_value = 'O valor do voucher não pode ser superior a 100%';
        }

        if (!values.units) {
          errors.units = 'A quantidade de vouchers é um campo obrigatório';
        } else if (Number(values.units) === 0) {
          errors.units = 'A quantidade de vouchers não pode ser zero';
        } else if (Number(values.units) < initialValues.units) {
          errors.units =
            'A quantidade de vouchers não pode ser inferior ao registrado';
        } else if (Number(values.units) < userPks.length) {
          errors.units =
            'A quantidade de vouchers não pode ser inferior ao número de usuários selecionados';
        } else if (Number(values.units) > MAX_QUANTITY_VOUCHER) {
          errors.units = `A quantidade máxima de vouchers é ${MAX_QUANTITY_VOUCHER}`;
        }

        if (showChargers && !values.charge_box_pks) {
          errors.charge_box_pks = 'O valor aplicável em é um campo obrigatório';
        }

        if (showNumberOfUses && !values.uses_per_user) {
          errors.uses_per_user =
            'A Qtd total de utilizações é um campo obrigatório';
        }

        if (!values.start) {
          errors.start = 'A data de início é um campo obrigatório';
        }

        if (!values.expire) {
          errors.expire = 'A data de expiração é um campo obrigatório';
        }

        if (voucherGroup === VoucherGroup.Guest && userPks.length === 0) {
          errors.user_pks =
            'Selecione ao menos um usuário para o voucher de convidado';
        }

        return errors;
      }}
      onSubmit={onSubmit}
    >
      {({
        handleSubmit,
        handleChange,
        setFieldValue,
        values,
        setFieldError,
        setFieldTouched,
        errors,
        submitCount,
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Row gutter={24}>
              <Col md={12}>
                <S.Label>Código do voucher</S.Label>
                <Input
                  name='code'
                  size='large'
                  disabled={loading}
                  readOnly={isEdit}
                  onChange={(event) => {
                    setFieldValue('code', event.target.value.toUpperCase());
                  }}
                  maxLength={25}
                  value={values.code}
                  onBlur={async () => {
                    setFieldTouched('code', true);

                    // na edicao o codigo nao muda: o proprio voucher acusaria o codigo como em uso
                    if (isEdit) return;

                    const hasCode: boolean = await handleVerifyCode(
                      values.code,
                    );

                    if (values.code?.length > 0 && hasCode) {
                      setFieldError('code', 'O código já está sendo utilizado');

                      setHasCode(true);
                    } else {
                      setHasCode(false);
                    }
                  }}
                  suffix={loadingCode && <LoadingOutlined />}
                />
                <ErrorMessage name='code' component={S.MessageBox} />
              </Col>

              <Col md={12}>
                <S.Label>Nome do voucher</S.Label>
                <Input
                  name='title'
                  size='large'
                  disabled={loading || hasCode}
                  readOnly={isEdit}
                  onChange={handleChange}
                  maxLength={50}
                  value={values.title}
                />
                <ErrorMessage name='title' component={S.MessageBox} />
              </Col>
            </Row>

            <Row gutter={24} style={{ marginTop: 24 }}>
              <Col md={12}>
                <S.Label>Descrição</S.Label>
                <Input
                  name='description'
                  size='large'
                  disabled={loading || hasCode}
                  onChange={handleChange}
                  maxLength={100}
                  value={values.description}
                />
                <ErrorMessage name='description' component={S.MessageBox} />
              </Col>

              <Col md={12}>
                <S.Label>Tipo do voucher</S.Label>
                <Select
                  placeholder=''
                  size='large'
                  style={{ width: '100%' }}
                  onChange={(value) => {
                    setFieldValue('kind', value);

                    setFieldValue('kind_value', '');

                    setFieldValue('expire_type', null);

                    setFieldTouched('kind_value', false);
                  }}
                  disabled={loading || hasCode}
                  open={isEdit ? false : undefined}
                  value={values.kind}
                >
                  <Select.Option value='PERCENT_RECHARGE'>
                    Porcentagem (%)
                  </Select.Option>
                  <Select.Option value='MONEY_RECHARGE'>
                    Reais (R$)
                  </Select.Option>
                  <Select.Option value='KWH_RECHARGE'>
                    Quilowatt-hora (kWh)
                  </Select.Option>
                </Select>
                <ErrorMessage name='kind' component={S.MessageBox} />
              </Col>
            </Row>

            <Row gutter={24} style={{ marginTop: 24 }}>
              <Col md={12}>
                <S.Label>Valor</S.Label>
                <Input
                  size='large'
                  disabled={loading || !values.kind || hasCode}
                  name='kind_value'
                  onChange={(event) => {
                    if (values.kind === 'MONEY_RECHARGE') {
                      setFieldValue(
                        'kind_value',
                        mascaraMoeda(event.target.value),
                      );
                    } else {
                      setFieldValue(
                        'kind_value',
                        formatNumber(event.target.value),
                      );
                    }
                  }}
                  value={
                    values.kind === 'MONEY_RECHARGE'
                      ? mascaraMoeda(String(values.kind_value))
                      : values.kind_value
                  }
                  onKeyDown={(event) => {
                    if (event.key === '-') event.preventDefault();
                  }}
                  suffix={getKindUnit(values.kind)}
                />
                <ErrorMessage name='kind_value' component={S.MessageBox} />
              </Col>

              <Col md={12}>
                <S.Label>Quantidade de vouchers</S.Label>

                <NumericFormat
                  disabled={loading || hasCode}
                  name='units'
                  customInput={Input}
                  onChange={(e) => {
                    if (e.target.value) {
                      setShowSelectUsersError(false);

                      setFieldValue('units', e.target.value);
                    }
                  }}
                  size='large'
                  onKeyDown={(event) => {
                    if (event.key === '-') event.preventDefault();
                  }}
                  value={values.units}
                />
                <ErrorMessage name='units' component={S.MessageBox} />
              </Col>
            </Row>

            <Row style={{ marginTop: 24, minHeight: 66 }} gutter={[24, 24]}>
              <Col md={12}>
                <S.Label style={{ marginBottom: 10 }}>Aplicável em</S.Label>
                <Radio.Group
                  onChange={() => setShowChargers(!showChargers)}
                  defaultValue={values.charge_box_pks.length ? 2 : 1}
                  disabled={loading || hasCode}
                >
                  <Radio value={1} style={{ fontWeight: 300 }}>
                    Todos os carregadores
                  </Radio>
                  <Radio value={2} style={{ fontWeight: 300 }}>
                    Carregador específico
                  </Radio>
                </Radio.Group>
              </Col>

              {showChargers && (
                <Col md={12}>
                  <S.Label>Selecione o carregador</S.Label>
                  <Select
                    mode='multiple'
                    allowClear
                    placeholder=''
                    size='large'
                    style={{ width: '100%' }}
                    onChange={(value) => setFieldValue('charge_box_pks', value)}
                    disabled={loading || hasCode}
                    filterOption={(input, option: any) =>
                      option.children
                        .join('')
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    defaultValue={
                      values.charge_box_pks.length
                        ? values.charge_box_pks
                        : undefined
                    }
                    loading={!chargePointsList.length}
                  >
                    {chargePointsList
                      .sort((a, b) =>
                        String(a?.description).localeCompare(
                          String(b?.description),
                        ),
                      )
                      .map((chargePoint) => (
                        <Select.Option
                          value={chargePoint.chargeBoxPk}
                          key={chargePoint.chargeBoxID}
                        >
                          {chargePoint.chargeBoxId}
                          {chargePoint.description
                            ? ` - ${chargePoint.description}`
                            : null}
                        </Select.Option>
                      ))}
                  </Select>
                  <ErrorMessage
                    name='charge_box_pks'
                    component={S.MessageBox}
                  />
                </Col>
              )}

              <Col md={12}>
                <S.Label style={{ marginBottom: 10 }}>Ativar voucher:</S.Label>

                <S.SwitchWrapper>
                  <Switch
                    onChange={(checked) => setFieldValue('active', checked)}
                    checked={values.active}
                  />
                </S.SwitchWrapper>
              </Col>

              <Col md={12}>
                <S.Label>Grupo do voucher</S.Label>
                <Select
                  placeholder=''
                  size='large'
                  style={{ width: '100%' }}
                  onChange={(value: VoucherGroup) => {
                    setVoucherGroup(value);

                    if (value === VoucherGroup.Activation) {
                      dispatch(setUsersVoucherList([]));
                    }
                  }}
                  disabled={loading || hasCode}
                  value={voucherGroup}
                >
                  <Select.Option value={VoucherGroup.Activation}>
                    Ativação
                  </Select.Option>
                  <Select.Option value={VoucherGroup.Guest}>
                    Convidado
                  </Select.Option>
                </Select>
              </Col>
            </Row>

            <Row gutter={24} style={{ marginTop: 24 }}>
              {values.kind === 'PERCENT_RECHARGE' && (
                <>
                  <Col md={showNumberOfUses ? 8 : 12}>
                    <S.Label style={{ marginBottom: 10 }}>
                      <S.Label>Voucher expira em:</S.Label>
                    </S.Label>
                    <Radio.Group
                      onChange={(e) => {
                        setFieldValue(
                          'expire_type',
                          e.target.value === 1
                            ? VoucherExpireType.ExpirationDate
                            : VoucherExpireType.UsesPerUser,
                        );

                        setFieldValue('uses_per_user', null);

                        setShowNumberOfUses(!showNumberOfUses);
                      }}
                      defaultValue={
                        values.expire_type === VoucherExpireType.UsesPerUser
                          ? 2
                          : 1
                      }
                    >
                      <Radio value={1} style={{ fontWeight: 300 }}>
                        Data de validade
                      </Radio>
                      <Radio value={2} style={{ fontWeight: 300 }}>
                        Qtd de utilizações
                      </Radio>
                    </Radio.Group>
                  </Col>

                  {showNumberOfUses && (
                    <Col md={4}>
                      <S.Label>Qtd total de utilizações</S.Label>
                      <NumericFormat
                        name='uses_per_user'
                        size='large'
                        customInput={Input}
                        onChange={handleChange}
                        maxLength={100}
                        defaultValue={values.uses_per_user}
                        value={values.uses_per_user}
                      />
                      <ErrorMessage
                        name='uses_per_user'
                        component={S.MessageBox}
                      />
                    </Col>
                  )}
                </>
              )}

              <Col md={6}>
                <S.Label>Data de início</S.Label>
                <DatePickerCustom
                  format='DD/MM/YYYY HH:mm'
                  showTime={{
                    hideDisabledOptions: true,
                    defaultValue: moment().startOf('day'),
                  }}
                  size='large'
                  name='start'
                  placeholder=''
                  onChange={(date: any) => {
                    setFieldValue(
                      'start',
                      moment(date).format('YYYY-MM-DD HH:mm:ss'),
                    );
                  }}
                  required
                  allowClear={false}
                  disabled={loading || hasCode}
                  maxLength={20}
                  value={values.start ? moment(values.start) : null}
                />
                <ErrorMessage name='start' component={S.MessageBox} />
              </Col>

              <Col md={6}>
                <S.Label>Válido até</S.Label>
                <DatePickerCustom
                  format='DD/MM/YYYY HH:mm'
                  showTime={{
                    hideDisabledOptions: true,
                    defaultValue: moment().endOf('day'),
                  }}
                  size='large'
                  name='expire'
                  placeholder=''
                  onChange={(date: any) => {
                    setFieldValue(
                      'expire',
                      moment(date).format('YYYY-MM-DD HH:mm:ss'),
                    );
                  }}
                  required
                  allowClear={false}
                  disabled={loading || hasCode}
                  maxLength={20}
                  value={values.expire ? moment(values.expire) : null}
                />

                <ErrorMessage name='expire' component={S.MessageBox} />
              </Col>
            </Row>
            {voucherGroup === VoucherGroup.Guest && (
              <Row gutter={24} style={{ marginTop: 24 }}>
                {userPks?.length > 0 && (
                  <Col
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginTop: 4,
                    }}
                  >
                    <S.BadgeContainer>{userPks?.length}</S.BadgeContainer>

                    <S.Label>
                      {userPks?.length > 1 ? 'Usuários' : 'Usuário'}
                    </S.Label>
                  </Col>
                )}

                <Col md={12}>
                  <S.Button
                    onClick={() => {
                      if (!values.units) {
                        return setShowSelectUsersError(true);
                      }

                      history.push('/vouchers/create/select_users');
                      onSaveChanges(values);
                    }}
                  >
                    <UsergroupAddOutlined />
                    Selecionar usuários
                  </S.Button>

                  {showSelectUsersError && (
                    <S.SelectUsersErrorLabel>
                      Preencha a quantidade do voucher
                    </S.SelectUsersErrorLabel>
                  )}

                  {submitCount > 0 && errors.user_pks && (
                    <S.SelectUsersErrorLabel>
                      {errors.user_pks as string}
                    </S.SelectUsersErrorLabel>
                  )}
                </Col>
              </Row>
            )}

            <Row style={{ marginTop: 32, fontWeight: 300 }}>
              Todos os campos são de preenchimento obrigatório
            </Row>

            <Row style={{ marginTop: 32, justifyContent: 'space-between' }}>
              <S.Button
                disabled={loading}
                size='large'
                danger
                onClick={handleCancel}
              >
                Cancelar
              </S.Button>

              <S.Button
                disabled={hasCode}
                loading={loading}
                key='submit'
                size='large'
                type='primary'
                htmlType='submit'
              >
                {isEdit ? 'Atualizar Voucher' : 'Criar Voucher'}
              </S.Button>
            </Row>
          </form>
        );
      }}
    </Formik>
  );
};

export default VoucherForm;
